import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';

const descricoesPorNatureza = {
    Alteração: ['Titularidade', 'Rateio', 'Enquadramento'],
    Solicitação: [
        'Segunda Via', 'Demonstrativo', 'Desligamento Definitivo', 'Danos Elétricos',
        'Aferição Medidor', 'Transferência de Saldo'
    ],
    Correção: ['Faturamento', 'Injetado'],
    'Rede Celpe': [
        'Falta de Energia', 'Qualidade', 'Religação', 'Falta de fase',
        'Desligamento Definitivo', 'Elo Fusível', 'Chave Faca', 'Sobretensão', 'Não Especificado'
    ]
};

const InserirProtocolos = () => {
    const [cc, setCC] = useState('');
    const [protocolo, setProtocolo] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [prazo, setPrazo] = useState('');
    const [statusProtocolo, setStatusProtocolo] = useState('');
    const [natureza, setNatureza] = useState('');
    const [descricao, setDescricao] = useState('');
    const [listDescricao, setListDescricao] = useState([]);
    const [responsavel, setResponsavel] = useState('');
    const [obs, setObs] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const selectNatureza = (e) => {
        const catAtual = e.target.value;
        setNatureza(catAtual);
        setListDescricao(descricoesPorNatureza[catAtual]);
        setDescricao('');
    };

    async function inserirProtocolos(e) {
        e.preventDefault();
        setIsSubmit(true);

        let dataInicio = '';
        if (dateTime) {
            setError(false);
            dataInicio = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            cc,
            protocolo,
            dataInicio,
            prazo,
            statusProtocolo,
            natureza,
            descricao,
            responsavel,
            obs
        });

        try {
            const response = await fetchFlask('fetchInserirProtocolos', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setCC('');
                setProtocolo('');
                setDateTime(null);
                setPrazo('');
                setStatusProtocolo('');
                setNatureza('');
                setDescricao('');
                setListDescricao([]);
                setResponsavel('');
                setObs('');
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const dataLabel = formatDateLabelPtBr(dateTime);
    const completedFields = [
        cc,
        protocolo,
        dateTime,
        prazo,
        statusProtocolo,
        natureza,
        descricao,
        responsavel
    ].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Inserir Protocolos</PageHeader>

            <form action='' method='POST' id='formInserirProtocolos' onSubmit={inserirProtocolos} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Cadastro de protocolo</h2>
                        </Box>
                        <p>
                            Registre protocolo, prazo, classificação e responsável para acompanhamento do atendimento junto à distribuidora.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <NoteAddRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <TextField
                                required
                                label='Conta Contrato'
                                value={cc}
                                onChange={(e) => setCC(e.target.value)}
                            />
                            <TextField
                                required
                                label='Protocolo'
                                value={protocolo}
                                onChange={(e) => setProtocolo(e.target.value)}
                            />
                            <TextField
                                label='Prazo (Dias)'
                                required
                                type='number'
                                inputProps={{ min: 0 }}
                                value={prazo}
                                onChange={(e) => setPrazo(e.target.value)}
                            />
                            <FormControl className='ui-control' error={error}>
                                <DayDatePicker
                                    id='dataInicio'
                                    label='Data de Início'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <DescriptionRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Classificação</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl required className='ui-control'>
                                <InputLabel id='labelStatusProtocolo'>Instância</InputLabel>
                                <Select
                                    id='statusProtocolo'
                                    labelId='labelStatusProtocolo'
                                    label='Instância'
                                    value={statusProtocolo}
                                    onChange={(e) => setStatusProtocolo(e.target.value)}
                                >
                                    <MenuItem value='EMAIL'>E-mail</MenuItem>
                                    <MenuItem value='TN'>TN</MenuItem>
                                    <MenuItem value='RECLAMAÇÃO'>Reclamação</MenuItem>
                                    <MenuItem value='OUVIDORIA'>Ouvidoria</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl required className='ui-control'>
                                <InputLabel id='labelNatureza'>Natureza</InputLabel>
                                <Select
                                    id='natureza'
                                    labelId='labelNatureza'
                                    label='Natureza'
                                    value={natureza}
                                    onChange={selectNatureza}
                                >
                                    <MenuItem value='Alteração'>Alteração</MenuItem>
                                    <MenuItem value='Solicitação'>Solicitação</MenuItem>
                                    <MenuItem value='Correção'>Correção</MenuItem>
                                    <MenuItem value='Rede Celpe'>Rede Celpe</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl required className='ui-control'>
                                <InputLabel id='labelDescricao'>Descrição</InputLabel>
                                <Select
                                    id='descricao'
                                    labelId='labelDescricao'
                                    label='Descrição'
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                >
                                    {listDescricao.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <SupportAgentRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Responsável e observação</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl required className='ui-control'>
                                <InputLabel id='labelResponsavel'>Responsável</InputLabel>
                                <Select
                                    id='responsavel'
                                    labelId='labelResponsavel'
                                    label='Responsável'
                                    value={responsavel}
                                    onChange={(e) => setResponsavel(e.target.value)}
                                >
                                    <MenuItem value='Anny'>Anny</MenuItem>
                                    <MenuItem value='Daniel'>Daniel</MenuItem>
                                    <MenuItem value='Eduardo'>Eduardo</MenuItem>
                                    <MenuItem value='Solluan'>Solluan</MenuItem>
                                    <MenuItem value='Elisa Souza'>Elisa</MenuItem>
                                    <MenuItem value='Cassia'>Cássia</MenuItem>
                                    <MenuItem value='Eduarda'>Eduarda</MenuItem>
                                    <MenuItem value='Adriana'>Adriana</MenuItem>
                                    <MenuItem value='Engenharia O&M'>Engenharia O&M</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label='Observação'
                                multiline
                                minRows={3}
                                value={obs}
                                onChange={(e) => setObs(e.target.value)}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do protocolo</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Conta contrato</span>
                            <strong className={!cc ? 'is-empty' : undefined}>{cc || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Protocolo</span>
                            <strong className={!protocolo ? 'is-empty' : undefined}>{protocolo || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Início</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{dataLabel}</strong>
                        </Box>
                        <Box>
                            <span>Prazo</span>
                            <strong className={!prazo ? 'is-empty' : undefined}>{prazo ? `${prazo} dias` : 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Instância</span>
                            <strong className={!statusProtocolo ? 'is-empty' : undefined}>{statusProtocolo || 'Não selecionada'}</strong>
                        </Box>
                        <Box>
                            <span>Natureza</span>
                            <strong className={!natureza ? 'is-empty' : undefined}>{natureza || 'Não selecionada'}</strong>
                        </Box>
                        <Box>
                            <span>Descrição</span>
                            <strong className={!descricao ? 'is-empty' : undefined}>{descricao || 'Não selecionada'}</strong>
                        </Box>
                        <Box>
                            <span>Responsável</span>
                            <strong className={!responsavel ? 'is-empty' : undefined}>{responsavel || 'Não selecionado'}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <BadgeRoundedIcon />
                        <p>
                            Confira instância, natureza e responsável antes de registrar. Esses dados guiam o acompanhamento do protocolo.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/8 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Inserir Protocolo
                    </SubmitButton>
                </Box>
            </form>
        </Box >
    );
};

export default InserirProtocolos;
